import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_WARDROBE_ITEM,
  GET_OUTFITS_BY_ITEM,
} from "@/graphql/queries/wardrobe";
import {
  UPDATE_WARDROBE_ITEM_TAGS,
  UPDATE_WARDROBE_ITEM,
} from "@/graphql/mutations/wardrobe";
import { DELETE_WARDROBE_ITEM } from "@/graphql/mutations/wardrobe";
import { GENERATE_OUTFIT_MUTATION } from "@/graphql/mutations/outfit";
import { useState, useEffect } from "react";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/24/outline";
import ChatInterface from "@/components/wardrobe/ChatInterface";
import ItemDetails from "@/components/wardrobe/ItemDetails";
import OutfitsList from "@/components/wardrobe/OutfitsList";
import { toast } from "react-hot-toast";
import { extractOccasion } from "@/utils/occasion";

interface ChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  action?: "generate_outfit" | "update_tags" | "update_colors";
}

export default function WardrobeItemDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State for chat interface
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  // State for inline editing
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedStyle, setEditedStyle] = useState("");
  const [editedCategory, setEditedCategory] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedColors, setEditedColors] = useState<string[]>([]);
  const [editedTags, setEditedTags] = useState<string[]>([]);
  const [newColor, setNewColor] = useState("");
  const [newTag, setNewTag] = useState("");

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_WARDROBE_ITEM, {
    variables: { id },
  });

  const {
    data: outfitsData,
    loading: outfitsLoading,
    refetch: refetchOutfits,
  } = useQuery(GET_OUTFITS_BY_ITEM, {
    variables: { itemId: id },
    skip: !id,
  });

  // Mutations
  const [updateWardrobeItemTags] = useMutation(UPDATE_WARDROBE_ITEM_TAGS, {
    onCompleted: () => refetch(),
  });

  const [updateWardrobeItem] = useMutation(UPDATE_WARDROBE_ITEM, {
    onCompleted: () => refetch(),
  });

  const [generateOutfit, { loading: generatingOutfit }] = useMutation(
    GENERATE_OUTFIT_MUTATION,
    {
      onCompleted: (data) => {
        if (data?.generateOutfit?.success && data?.generateOutfit?.outfit?.id) {
          addMessage(
            "assistant",
            `Perfect! I've created a stunning outfit for you. Let's take a look!`
          );
          setTimeout(() => {
            navigate(`/lookbook/${data.generateOutfit.outfit.id}`);
          }, 1500);
        }
      },
    }
  );

  const [deleteWardrobeItem, { loading: deleting }] = useMutation(
    DELETE_WARDROBE_ITEM,
    {
      onCompleted: (res) => {
        if (res?.deleteWardrobeItem?.success) {
          toast.success("Item deleted");
          navigate("/wardrobe");
        } else {
          toast.error(res?.deleteWardrobeItem?.message || "Failed to delete");
        }
      },
      onError: () => toast.error("Failed to delete"),
    }
  );

  const outfitItem = data?.getWardrobeItem?.outfitItem;
  const outfits = outfitsData?.getOutfitsByItem?.outfits || [];

  // Initialize editing values when data loads
  useEffect(() => {
    if (outfitItem) {
      setEditedStyle(outfitItem.style || "");
      setEditedCategory(outfitItem.category?.name || "");
      setEditedDescription(outfitItem.description || "");
      setEditedColors(outfitItem.colors || []);
      setEditedTags(outfitItem.tags || []);
    }
  }, [outfitItem]);

  // Initialize chat with welcome message
  useEffect(() => {
    if (outfitItem && messages.length === 0) {
      const welcomeMessage = `Hello! I'm your personal stylist assistant. I see you're looking at your ${
        outfitItem.description || "beautiful piece"
      }. How can I help you style this today?`;

      setMessages([
        {
          id: "1",
          type: "assistant",
          content: welcomeMessage,
          timestamp: new Date(),
        },
      ]);
    }
  }, [outfitItem, messages.length]);

  const addMessage = (
    type: "user" | "assistant",
    content: string,
    action?: string
  ) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: new Date(),
      action: action as any,
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    addMessage("user", inputMessage);
    setIsTyping(true);

    setTimeout(() => {
      handleAIResponse(inputMessage);
      setIsTyping(false);
    }, 1000);

    setInputMessage("");
  };

  // Occasion parsing is handled by `extractOccasion` from `src/utils/occasion.ts`

  const handleAIResponse = (userMessage: string) => {
    // Define patterns for different occasions
    const occasionPatterns = [
      /\b(work|office|meeting|professional|business)\b/i,
      /\b(casual|relaxed|everyday|comfortable)\b/i,
      /\b(date\s+night|date|romantic|dinner|restaurant)\b/i,
      /\b(cocktail\s+party|party|celebration|event|social)\b/i,
      /\b(formal|semi[-\s]?formal|black[-\s]?tie|smart\s+casual|fancy|dressy)\b/i,
      /\b(vacation|travel|holiday\s+party|holiday|trip|christmas|halloween|new\s*year(?:'s)?)\b/i,
      /\b(gym|workout|exercise|fitness)\b/i,
      /\b(beach|garden|rooftop|outdoor|indoor|picnic)\b/i,
      /\b(summer|winter|spring|fall|autumn|cold|warm|cozy)\b/i,
      /\b(brunch|lunch|breakfast|coffee)\b/i,
      /\b(wedding|reception|ceremony|nuptials)\b/i,
      /\b(interview|presentation|conference)\b/i,
      /\b(funeral|memorial)\b/i,
      /\b(graduation)\b/i,
      /\b(photoshoot|photo\s+shoot)\b/i,
      /\b(baby\s+shower|bridal\s+shower)\b/i,
      /\b(festival)\b/i,
    ];

    // Check if message contains an occasion
    const hasOccasion = occasionPatterns.some((pattern) =>
      pattern.test(userMessage)
    );

    if (hasOccasion) {
      // Extract the specific occasion and use it
      const extractedOccasion = extractOccasion(userMessage);

      // User provided an occasion, generate the outfit
      generateOutfit({
        variables: {
          input: {
            baseItemId: id,
            occasion: extractedOccasion,
            chatContext: userMessage,
          },
        },
      });
      addMessage(
        "assistant",
        `Perfect! I'm creating a stylish outfit with your ${outfitItem?.description} for that occasion. This will just take a moment...`
      );
    } else {
      // No clear occasion found, ask for clarification in a friendly way
      addMessage(
        "assistant",
        `I'd love to help you style your ${outfitItem?.description}! To create the perfect outfit, could you tell me what occasion you're dressing for? For example, you could say something like "casual day out", "work meeting", "dinner date", or whatever you have in mind.`
      );
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    action: () => void
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      action();
    }
  };

  // Editing functions
  const startEditing = (field: string) => {
    setEditingField(field);
  };

  const cancelEditing = () => {
    setEditingField(null);
    // Reset edited values to original
    if (outfitItem) {
      setEditedStyle(outfitItem.style || "");
      setEditedCategory(outfitItem.category?.name || "");
      setEditedDescription(outfitItem.description || "");
      setEditedColors(outfitItem.colors || []);
      setEditedTags(outfitItem.tags || []);
    }
    setNewColor("");
    setNewTag("");
  };

  const saveField = async (field: string) => {
    try {
      if (
        field === "style" ||
        field === "category" ||
        field === "description" ||
        field === "colors"
      ) {
        const input: any = {};

        if (field === "style") {
          input.style = editedStyle;
        } else if (field === "category") {
          input.categoryName = editedCategory;
        } else if (field === "description") {
          input.description = editedDescription;
        } else if (field === "colors") {
          input.colors = editedColors;
        }

        await updateWardrobeItem({
          variables: {
            id,
            input,
          },
        });
      } else if (field === "tags") {
        await updateWardrobeItemTags({
          variables: {
            id,
            tags: editedTags,
          },
        });
      }
      setEditingField(null);
      setNewColor("");
      setNewTag("");
    } catch (error) {
      console.error("Error saving field:", error);
    }
  };

  const addColor = () => {
    if (newColor.trim() && !editedColors.includes(newColor.trim())) {
      setEditedColors([...editedColors, newColor.trim()]);
      setNewColor("");
    }
  };

  const removeColor = (index: number) => {
    setEditedColors(editedColors.filter((_, i) => i !== index));
  };

  const addTag = () => {
    if (newTag.trim() && !editedTags.includes(newTag.trim())) {
      setEditedTags([...editedTags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (index: number) => {
    setEditedTags(editedTags.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 font-light tracking-wider uppercase">
            Loading your wardrobe item...
          </p>
        </div>
      </div>
    );
  }

  if (error || !outfitItem) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 font-light tracking-wider uppercase mb-4">
            Error loading item
          </p>
          <button
            onClick={() => navigate("/wardrobe")}
            className="px-6 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl hover:scale-105 transition-transform duration-200 font-light tracking-wider uppercase"
          >
            Back to Wardrobe
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate("/wardrobe")}
              className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span className="font-light tracking-wider uppercase">
                Back to Wardrobe
              </span>
            </button>
            <h1 className="text-xl font-light tracking-wider uppercase text-gray-900 dark:text-white">
              {outfitItem.description || "Wardrobe Item"}
            </h1>
            <div className="flex items-center justify-end w-32">
              <button
                aria-label="Delete Item"
                disabled={deleting}
                onClick={() => {
                  if (!id) return;
                  const confirmed = window.confirm(
                    "Delete this item? This cannot be undone."
                  );
                  if (!confirmed) return;
                  deleteWardrobeItem({ variables: { id } });
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-light tracking-wider uppercase border border-gray-300 dark:border-gray-700 bg-gradient-to-br from-gray-100 to-white dark:from-gray-800 dark:to-gray-900 text-gray-700 dark:text-gray-200 hover:scale-105 transition-transform disabled:opacity-60"
              >
                <TrashIcon className="h-4 w-4" />
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Column - Item Details */}
          <div className="space-y-8" id="left-column">
            <ItemDetails
              outfitItem={outfitItem}
              isFavorited={isFavorited}
              setIsFavorited={setIsFavorited}
              editingField={editingField}
              onStartEditing={startEditing}
              onCancelEditing={cancelEditing}
              onSaveField={saveField}
              onInputKeyDown={handleInputKeyDown}
              editedStyle={editedStyle}
              setEditedStyle={setEditedStyle}
              editedCategory={editedCategory}
              setEditedCategory={setEditedCategory}
              editedDescription={editedDescription}
              setEditedDescription={setEditedDescription}
              editedColors={editedColors}
              setEditedColors={setEditedColors}
              editedTags={editedTags}
              setEditedTags={setEditedTags}
              newColor={newColor}
              setNewColor={setNewColor}
              newTag={newTag}
              setNewTag={setNewTag}
              onAddColor={addColor}
              onRemoveColor={removeColor}
              onAddTag={addTag}
              onRemoveTag={removeTag}
            />

            <OutfitsList outfits={outfits} loading={outfitsLoading} />
          </div>

          {/* Right Column - Chat Interface */}
          <div className="lg:sticky lg:top-[15rem]">
            <div className="lg:h-[calc(100vh-15rem)]">
              <ChatInterface
                messages={messages}
                inputMessage={inputMessage}
                setInputMessage={setInputMessage}
                isTyping={isTyping}
                generatingOutfit={generatingOutfit}
                onSendMessage={handleSendMessage}
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
